import { FunctionComponent, useEffect, useState } from "react";
import { Facilitator } from "../facilitator";
import { Box, Button, Card, CardContent, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, styled, TextField, Typography } from "@mui/material";
import FacilitatorService from "../facilitatorService";
import { ISMSTemplate } from "../models/ISchoolModel";
import { toast } from "react-toastify";
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});
export const SendSMSComponent: FunctionComponent = () => {
    const [smsTemplates, setSMSTemplates] = useState<ISMSTemplate[]>([]);
    const [selectedSmsTemplate, setSelectedSmsTemplate] = useState<ISMSTemplate | null>(null);
    const [smsTriggerInProgress, setSmsTriggerInProgress] = useState<boolean>(false);
    const [templateSampleFile, SetTemplateSampleFile] = useState<File | null>(null);
    const loadSmsTemplates = () => {
        FacilitatorService.getSmsTemplates().then((res) => {
            if (res?.data?.errors?.length > 0) {
                res?.data?.errors.forEach((i) => toast.error(i.message));
            }
            setSMSTemplates(res.data.list);
            setSelectedSmsTemplate(res.data.list.length > 0 ? res.data.list[0] : null);
        }).catch(() => {
            toast.error("Failed to load SMS templates");
        });
    }
    const handleTemplateChange = (event: any) => {
        const templateId = event.target.value;
        const selectedTemplate = smsTemplates.find(template => template.templateId === templateId) || null;
        setSelectedSmsTemplate(selectedTemplate);
    };

    useEffect(() => {

        loadSmsTemplates();
        // Any initialization logic can go here
    }, []);
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        let uploadFiles = e.target.files;
        if (uploadFiles == null || uploadFiles.length === 0) {
            return;
        }
        SetTemplateSampleFile(uploadFiles[0]);
    }
    const handleFileDelete = () => {
        SetTemplateSampleFile(null);
    }

    const handleTriggerSMS = () => {
        setSmsTriggerInProgress(true);
        // Logic to trigger SMS sending can go here
        FacilitatorService.sendSMS(templateSampleFile as File, selectedSmsTemplate as ISMSTemplate).then((res) => {
            if (res?.data?.errors?.length > 0) {
                res?.data?.errors.forEach((i) => toast.error(i.message));
            } else {
                toast.info("SMS Triggered successfully!");
            }
        }).catch(() => {
            toast.error("Failed to trigger SMS");
        }).finally(() => {
            SetTemplateSampleFile(null);
            setSmsTriggerInProgress(false);
        });

    }
    return (
        <Facilitator>
            <Grid container>
                <Grid item xs={12}>
                    <Card sx={{ height: '100%' }}>
                        {/* Page header */}
                        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Send SMS</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    Select a template, upload your recipient file, and trigger the SMS campaign
                                </Typography>
                            </Box>
                        </Box>
                        <Divider />
                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={3}>

                                {/* Step 1 — Template */}
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                                                Step 1 — Select Template
                                            </Typography>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <FormControl fullWidth>
                                                <InputLabel id="sms-template-label">Template</InputLabel>
                                                <Select
                                                    labelId="sms-template-label"
                                                    id="sms-template-select"
                                                    value={selectedSmsTemplate ? selectedSmsTemplate.templateId : ''}
                                                    label="Template"
                                                    onChange={handleTemplateChange}
                                                >
                                                    {smsTemplates.length === 0 && <MenuItem value="" disabled>No templates available</MenuItem>}
                                                    {smsTemplates.map((template) => (
                                                        <MenuItem key={template.templateId} value={template.templateId}>{template.templateName}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <TextField
                                                label="Template Message"
                                                multiline
                                                rows={4}
                                                fullWidth
                                                value={selectedSmsTemplate ? selectedSmsTemplate.message : ''}
                                                InputProps={{ readOnly: true }}
                                                variant="outlined"
                                            />

                                            <Button
                                                variant="outlined"
                                                startIcon={<DownloadIcon />}
                                                href={selectedSmsTemplate ? selectedSmsTemplate.templateFileUrl : '#'}
                                                target="_blank"
                                                disabled={!selectedSmsTemplate}
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                Download Template File
                                            </Button>
                                        </Box>
                                    </Card>
                                </Grid>

                                {/* Step 2 — Upload & Send */}
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                                                Step 2 — Upload &amp; Send
                                            </Typography>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, height: 'calc(100% - 57px)' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Upload a filled recipient file matching the selected template format, then trigger the SMS.
                                            </Typography>

                                            <Button
                                                component="label"
                                                role={undefined}
                                                variant="outlined"
                                                tabIndex={-1}
                                                startIcon={<CloudUploadIcon />}
                                                size="large"
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                {templateSampleFile ? 'Replace File' : 'Upload Recipient File'}
                                                <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
                                            </Button>

                                            {templateSampleFile
                                                ? <Chip label={templateSampleFile.name} color="primary" variant="outlined" onDelete={handleFileDelete} sx={{ alignSelf: 'flex-start', maxWidth: '100%' }} />
                                                : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No file chosen</Typography>
                                            }

                                            <Box sx={{ mt: 'auto', pt: 2 }}>
                                                <Divider sx={{ mb: 2 }} />
                                                <LoadingButton
                                                    variant="contained"
                                                    size="large"
                                                    onClick={handleTriggerSMS}
                                                    loading={smsTriggerInProgress}
                                                    disabled={!templateSampleFile || !selectedSmsTemplate}
                                                    endIcon={<SendIcon />}
                                                    sx={{ fontWeight: 600, px: 4 }}
                                                >
                                                    Send SMS
                                                </LoadingButton>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Facilitator>
    );
}